import { useEffect, useMemo, useState } from 'react';
import { HStack, VStack, useTheme, IconButton, ScrollView, useBreakpointValue } from 'native-base';
import { clone, filter, isEqual, inRange, map, partition, range, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, CloseIcon, Text } from '@pimm/base';
import { useWidgetModules, WidgetModule } from '../context/widget-modules.context';
import { WidgetSlot } from './widget-slot';

type WidgetEditorProps = {
  _container?: React.ComponentProps<typeof VStack>;
  onClose: () => void;
};

export const WidgetEditor = ({ _container, onClose }: WidgetEditorProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { widgets, updateWidgets } = useWidgetModules();
  const [list, setList] = useState<{ favorites: WidgetModule[]; availables: WidgetModule[] }>({ favorites: [], availables: [] });
  const width = useBreakpointValue({ base: 'full', md: '1/4', lg: '1/5', xl: '1/6' });

  useEffect(() => {
    if (widgets.length === 0) return;
    const [favorites, availables] = partition(widgets, _ => _.isFavorite);
    setList({ favorites: orderBy(favorites, ['favoriteSeq'], ['asc']), availables });
  }, [widgets]);

  const handleSaveFavorite = async () => {
    updateWidgets(list.favorites);
    onClose();
  };

  const handleAddFavorite = (module: WidgetModule) => {
    if (list.favorites.length >= 6) return; // Max 6 favorites
    const updatedModule = {
      ...module,
      isFavorite: true,
    };
    setList(prev => ({
      favorites: [...prev.favorites, updatedModule],
      availables: filter(prev.availables, _ => _.identifier !== module.identifier),
    }));
  };

  const handleRemoveFavorite = (module: WidgetModule) => {
    const updatedModule = {
      ...module,
      isFavorite: false,
    };
    setList(prev => ({
      favorites: filter(prev.favorites, _ => _.identifier !== module.identifier),
      availables: [...prev.availables, updatedModule],
    }));
  };

  const handleMoveFavorite = (currentIndex: number, direction: 'left' | 'right') => {
    setList(prev => {
      const swapIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
      if (!inRange(swapIndex, 0, prev.favorites.length)) return prev;
      const favorites = clone(prev.favorites);
      [favorites[currentIndex], favorites[swapIndex]] = [favorites[swapIndex], favorites[currentIndex]];
      return { ...prev, favorites };
    });
  };

  const initialFavoriteIds = useMemo(
    () => map(orderBy(filter(widgets, m => m.isFavorite !== false), ['favoriteSeq'], ['asc']), m => m.identifier ?? ''),
    [widgets],
  );
  const hasChanges = !isEqual(initialFavoriteIds, map(list.favorites, m => m.identifier ?? ''));

  return (
    <VStack flex={1} bgColor='gray.100' rounded='2xl' {..._container}>
      <HStack p={2} justifyContent="space-between" alignItems="center" bgColor='white' roundedTop='2xl'>
        <Text size="xl" color='black' fontWeight={700}>
          {t('widgets:favorite_widgets_editor')}
        </Text>
        <IconButton rounded="sm" onPress={onClose} icon={<CloseIcon size={4} color={colors.gray[700]} />} />
      </HStack>

      <ScrollView flex={1} px={2} mt={2} >
        <VStack w='full' space={2}>
          <Text size="xl" fontWeight={700} color='black' >
            {t('widgets:favorite_widgets_editor_description')}
          </Text>
          <HStack w='full' flexWrap='wrap' alignItems='flex-start' justifyContent='flex-start'>
            {range(6).map((index) => (
              <WidgetSlot
                key={index}
                w={width}
                mb={3}
                module={list?.favorites?.[index]}
                index={index}
                totalFavorites={list.favorites.length}
                onMove={handleMoveFavorite}
                onRemove={handleRemoveFavorite}
              />
            ))}
          </HStack>
        </VStack>
        <VStack w='full' space={2} mt={3}>
          <Text size="xl" fontWeight={700} color='black' >
            {t('widgets:available_widgets')}
          </Text>
          <HStack w='full' flexWrap='wrap'>
            {map(list?.availables, (module, i) => (
              <WidgetSlot
                key={`available-${module?.identifier}.${i}`}
                w={width}
                mb={3}
                module={module}
                canAdd={list.favorites.length < 6}
                onAdd={handleAddFavorite}
              />
            ))}
          </HStack>
        </VStack>
      </ScrollView>

      <HStack p={2} w='full' h='70px' bgColor='white' roundedBottom='2xl' alignItems='center' justifyContent='flex-end' space={3}>
        <Button variant="unstyled" minWidth={120} onPress={onClose}>
          {t('widgets:cancel')}
        </Button>
        <Button
          minWidth={120}
          onPress={handleSaveFavorite}
          isDisabled={!hasChanges}
        >
          {t('widgets:save_changes')}
        </Button>
      </HStack>
    </VStack>
  );
};
